import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Button from '../Button';
import './RecaptchaErrorBoundary.scss';

const RecaptchaFallbackUI = ({ onRetry, message, showRefreshButton }) => (
  <div className='recaptcha-error-fallback'>
    <h1 className='recaptcha-error-fallback__title'>
      Security Verification Issue
    </h1>
    <p className='recaptcha-error-fallback__message'>{message}</p>
    <div className='recaptcha-error-fallback__actions'>
      <Button text='Try Again' onClick={onRetry} />
      {showRefreshButton && (
        <Button
          text='Refresh Page'
          variant='secondary'
          onClick={() => window.location.reload()}
        />
      )}
    </div>
  </div>
);

RecaptchaFallbackUI.propTypes = {
  onRetry: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  showRefreshButton: PropTypes.bool
};

class ErrorBoundaryInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.warn('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

ErrorBoundaryInternal.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node.isRequired
};

const RecaptchaErrorBoundary = ({
  children,
  onRetry,
  showRefreshButton
}) => {
  const [asyncError, setAsyncError] = useState(null);
  const [errorCount, setErrorCount] = useState(0);

  useEffect(() => {
    const handleUnhandledRejection = event => {
      const error = event.reason;
      const errorMessage = error?.message || error || 'Unknown error';

      const isRecaptchaError =
        errorMessage.includes('recaptcha') ||
        errorMessage.includes('reCAPTCHA') ||
        errorMessage.includes('Timeout') ||
        errorMessage.includes('timeout');

      if (isRecaptchaError && errorCount < 3) {
        console.warn('Caught reCAPTCHA-related error:', errorMessage);
        event.preventDefault();

        setAsyncError({
          message:
            'Security verification encountered an issue. Please refresh and try again.'
        });
        setErrorCount(prev => prev + 1);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [errorCount]);

  const handleRetry = () => {
    setAsyncError(null);
    setErrorCount(0);
    if (onRetry) {
      onRetry();
    }
  };

  const fallback = (
    <RecaptchaFallbackUI
      onRetry={handleRetry}
      message={
        asyncError?.message ||
        'An unexpected error occurred. Please try again.'
      }
      showRefreshButton={showRefreshButton}
    />
  );

  if (asyncError) {
    return fallback;
  }

  return (
    <ErrorBoundaryInternal fallback={fallback}>{children}</ErrorBoundaryInternal>
  );
};

RecaptchaErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  onRetry: PropTypes.func,
  showRefreshButton: PropTypes.bool
};

RecaptchaErrorBoundary.defaultProps = {
  onRetry: () => { },
  showRefreshButton: true
};

export default RecaptchaErrorBoundary; 